// Stepper — the full-page wizard SHELL (ADR-038): step list + progress + per-step
// validation gating Next + always-free Back, for complex / branching / infrequent
// creates (a user, a site). Hand-rolled (native-owned bucket, ADR-028 — a numbered
// list + nav buttons, no focus-engine to import); composes Button.
//
// TWO WIZARD KINDS ride this one shell (ADR-038):
//   • ATOMIC — one RHF form across steps (useFormShell), values accumulated
//     client-side (RHF retains unmounted steps' values), ONE atomic submit on
//     the last step (the default `onSubmit` + `submitLabel` path — user-create).
//   • DRAFT-BACKED — the entity's own draft row IS the persistence: the first
//     step's Next creates it, each later Next commits its step (onValidateStep
//     validates AND persists), and the wizard may end in a decision cluster
//     (`endActions` — e.g. "Finish as draft" · "Activate") instead of the single
//     submit; `footerStart` carries the exit affordance ("Save and exit"). Both
//     slots are ReactNode (slots-over-flags) — omitting them IS the atomic wizard.
//
// Scope (locked): linear flows; step index is component state (NOT URL — it's
// transient progress); count visible up front; Next gated by onValidateStep; Back
// always free. Draft persistence is never wizard-state — it is the draft entity.
// The chrome-less focused-route FRAME is the wizard route's job (the layout around
// this), not the Stepper's; the route-leave guard rides the shell (useFormRouteLeave).

import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { icons } from './icons.js';
import { Button } from './Button.js';
// The Stepper is the full-page focused-wizard SHELL ([ADR-038]); FocusedLayout is its
// designated frame — so it renders its nav into that frame's sticky footer-slot
// (always reachable), falling back to inline when used outside a FocusedLayout.
import { FocusedFooter, useFocusedFooter } from '../layout/FocusedLayout.js';

export interface StepperStep {
  /** Stable id. */
  id: string;
  /** Visible step label in the indicator + the step heading (i18n'd by the caller). */
  label: string;
  /** This step's fields. Only the current step mounts; RHF retains the rest. */
  content: ReactNode;
  /** Optional step: marked "(optional)" in the indicator + heading, and the
   *  footer gains Skip (Back · Skip · Next — FRONTEND-PATTERNS §Footer actions),
   *  which advances WITHOUT running onValidateStep (nothing commits). */
  optional?: boolean;
  /** Disable Next on this step — the step is known-blocked BEFORE the click, so
   *  Next reads disabled instead of silently no-op'ing (e.g. an import preview
   *  with errors). `onValidateStep` still guards; this is the visual truth. */
  nextDisabled?: boolean;
  /** Hide Back on this step — for a TERMINAL step whose prior step already
   *  committed an irreversible action (the import Result: going Back to the
   *  committed Preview only invites a re-run). The only way out is the ending
   *  action (Done) or Close. Not for a review-before-submit last step, which
   *  keeps Back to edit earlier steps. */
  hideBack?: boolean;
}

export interface StepperProps {
  steps: StepperStep[];
  /** Validate the current step before Next advances; resolve false to block.
   *  (Wire to RHF: `(i) => form.trigger(fieldsForStep[i])`.) Back is never gated. */
  onValidateStep?: (currentIndex: number) => boolean | Promise<boolean>;
  /** The atomic submit, fired by the final step's primary button. Wire to the
   *  shell: `() => void shell.onFormSubmit()` (full-schema validation + submit). */
  onSubmit: () => void;
  /** The submit verb on the final step ("Create user") — i18n'd by the caller. */
  submitLabel: string;
  /** Final-submit in flight — locks nav + spins the submit button. */
  isSubmitting?: boolean;
  /** Accessible name for the step-list (e.g. "Create user steps"). */
  ariaLabel?: string;
  /** Notified when the active step changes (0-based). */
  onStepChange?: (index: number) => void;
  /** Draft-backed wizards: REPLACES the single submit button on the last step
   *  with a decision cluster ("Finish as draft" · "Activate"). When set,
   *  `onSubmit`/`submitLabel` are not rendered — the cluster owns the ending. */
  endActions?: ReactNode;
  /** Leading footer slot on every step (logical start) — the draft-backed
   *  wizard's "Save and exit". Info/exit only; the primary stays at the end. */
  footerStart?: ReactNode;
  /** Open at this step (0-based, clamped) — resume-at-first-incomplete for
   *  draft-backed wizards. Initial only; never re-applied after mount. */
  initialStep?: number;
  /** CONTROLLED step (0-based): the host owns it — a review step's "Change" jumps
   *  back to a step through it. With `step` set, every move (Back · Next · Skip)
   *  is REPORTED through `onStepChange` and applied by the host. */
  step?: number;
}

// Nearest scrollable ancestor (or null) — used to land each step at the scroll
// region's TOP so the page heading rendered above the wizard stays visible.
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
}

export function Stepper({
  steps,
  onValidateStep,
  onSubmit,
  submitLabel,
  isSubmitting = false,
  ariaLabel,
  onStepChange,
  endActions,
  footerStart,
  initialStep = 0,
  step,
}: StepperProps) {
  const { t } = useTranslation('common');
  const clamp = (i: number) => Math.min(Math.max(0, i), steps.length - 1);
  const [inner, setInner] = useState(() => clamp(initialStep));
  const current = step === undefined ? inner : clamp(step);
  // One mover for both modes: uncontrolled keeps the step here; controlled reports it and the host applies it.
  const move = (to: (c: number) => number) => {
    const next = clamp(to(current));
    if (step === undefined) setInner(next);
    else onStepChangeRef.current?.(next);
  };
  const [advancing, setAdvancing] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const skipInitialFocus = useRef(true);
  const headingId = useId();
  const footerTarget = useFocusedFooter();

  // onStepChange via a ref so the step-change effect below depends ONLY on `current`.
  // Callers pass an inline handler (a fresh identity every render); keeping it in the
  // effect deps re-ran the effect on EVERY render, and after mount that re-fired
  // regionRef.focus() — stealing focus out of the field the user was typing in the
  // instant the form went dirty (the first keystroke's re-render). Same ref pattern
  // as ContextPanel's onClose.
  const onStepChangeRef = useRef(onStepChange);
  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  });

  const isFirst = current === 0;
  const isLast = current === steps.length - 1;

  // Land each step (and the initial open) at ITS top — the step indicator + heading,
  // not wherever the prior view was scrolled or where region-focus would otherwise
  // land (which scrolled past the header). Move SR focus to the new step region on
  // change only (not initial mount), with preventScroll so it doesn't re-scroll.
  useEffect(() => {
    // Land at the wizard's TOP so the page heading stays visible: scroll the
    // enclosing region to 0. Scrolling the step root INTO view instead pinned it
    // to the top and hid the h1 + draft strip rendered above it. The Stepper is
    // the full-page wizard shell (ADR-038), so the region's top IS the wizard's.
    const scroller = getScrollParent(rootRef.current);
    if (scroller) scroller.scrollTo({ top: 0 });
    else rootRef.current?.scrollIntoView({ block: 'start' });
    if (skipInitialFocus.current) {
      skipInitialFocus.current = false;
      return;
    }
    regionRef.current?.focus({ preventScroll: true });
    // Uncontrolled: the host learns of the change here; controlled: it already knows (it applied it).
    if (step === undefined) onStepChangeRef.current?.(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on the step's change alone
  }, [current]);

  const goNext = async () => {
    if (onValidateStep) {
      setAdvancing(true);
      let ok = false;
      try {
        ok = await onValidateStep(current);
      } finally {
        setAdvancing(false);
      }
      if (!ok) return;
    }
    move((c) => c + 1);
  };

  const goBack = () => move((c) => c - 1);

  // Skip an optional step: advance with NO validation and NO commit.
  const goSkip = () => move((c) => c + 1);

  const currentStep = steps[current];
  if (!currentStep) return null; // empty steps / out-of-range guard (noUncheckedIndexedAccess)

  const navButtons = (
    <>
      {!currentStep.hideBack && (
        <Button variant="secondary" onClick={goBack} disabled={isFirst || isSubmitting}>
          {t('stepper.back')}
        </Button>
      )}
      {currentStep.optional && !isLast && (
        <Button variant="ghost" onClick={goSkip} disabled={isSubmitting || advancing}>
          {t('stepper.skip')}
        </Button>
      )}
      {isLast ? (
        (endActions ?? (
          <Button type="button" variant="primary" onClick={onSubmit} loading={isSubmitting}>
            {submitLabel}
          </Button>
        ))
      ) : (
        <Button type="button" variant="primary" onClick={goNext} loading={advancing} disabled={currentStep.nextDisabled}>
          {t('stepper.next')}
        </Button>
      )}
    </>
  );

  return (
    <div ref={rootRef} className="flex flex-col gap-6">
      {/* Step indicator — the step list AND the progress. Count is also stated as
          text for SR + low-vision (never colour-only: state rides the number/check
          + weight). */}
      <div>
        <ol className="flex items-center" aria-label={ariaLabel}>
          {steps.map((step, i) => {
            const done = i < current;
            const active = i === current;
            return (
              <Fragment key={step.id}>
                {i > 0 && (
                  <li
                    aria-hidden
                    className={`mx-2 h-px flex-1 ${i <= current ? 'bg-[var(--color-primary-display)]' : 'bg-[var(--color-border)]'}`}
                  />
                )}
                <li
                  className="flex shrink-0 items-center gap-2"
                  aria-current={active ? 'step' : undefined}
                >
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium ${
                      done || active
                        ? 'bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)]'
                        : 'bg-[var(--color-bg-surface-alt)] text-[var(--color-text-secondary)] border border-[var(--color-border)]'
                    }`}
                  >
                    {done ? <icons.check size={15} weight="bold" aria-hidden /> : i + 1}
                  </span>
                  {/* Labels hide on narrow — the numbered dots + connectors + the
                      "Step X of Y" text + the current-step <h2> carry the state, so
                      3+ steps never overflow a phone. Full labels return ≥ sm. */}
                  <span
                    className={`hidden sm:inline text-sm ${
                      active
                        ? 'font-semibold text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-secondary)]'
                    }`}
                  >
                    {step.label}
                  </span>
                </li>
              </Fragment>
            );
          })}
        </ol>
        <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
          {t('stepper.progress', { current: current + 1, total: steps.length })}
        </p>
      </div>

      {/* Current step content — a focusable, labelled region so step changes land
          SR focus on the new step. RHF retains the other steps' values while
          unmounted (shouldUnregister defaults off). */}
      <div ref={regionRef} tabIndex={-1} aria-labelledby={headingId} className="outline-none">
        {/* The step name is spoken by the INDICATOR from sm up (labels are
            `hidden sm:inline` there) — repeating it here doubled every step's
            title on desktop. Mobile keeps the visible heading (the indicator
            compresses to numbered dots); sr-only above keeps the region's
            accessible name at every width. */}
        <h2 id={headingId} className="mb-4 text-base font-semibold text-[var(--color-text-primary)] sm:sr-only">
          {currentStep.label}
        </h2>
        {currentStep.content}
      </div>

      {/* Nav — footerStart (logical start, exit/info) · Back → Next / the ending
          (end). Under FocusedLayout it rides the sticky footer-slot (always
          reachable through a long step); else inline. */}
      {footerTarget ? (
        <FocusedFooter>
          <div className="flex items-center gap-2">
            {footerStart && <div className="me-auto flex items-center gap-2">{footerStart}</div>}
            <div className={`flex items-center gap-2 ${footerStart ? '' : 'ms-auto'}`}>{navButtons}</div>
          </div>
        </FocusedFooter>
      ) : (
        // Inline nav (outside a FocusedLayout — e.g. the ImportWizard in a Modal).
        // Sticky to the bottom of the scrolling container so Back/Next stay reachable
        // when the step content is long (a big import error list); the opaque overlay
        // background keeps scrolling content from showing through.
        <div className="sticky bottom-0 -mx-5 -mb-4 flex items-center gap-2 border-t border-[var(--color-border)] bg-[var(--color-bg-overlay)] px-5 py-4">
          {footerStart && <div className="me-auto flex items-center gap-2">{footerStart}</div>}
          <div className={`flex items-center gap-2 ${footerStart ? '' : 'ms-auto'}`}>{navButtons}</div>
        </div>
      )}
    </div>
  );
}
